import { useMemo, useEffect } from 'react';
import { useQuery } from 'data-fetching-client';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingSpinnerCentered from 'automation-ui-formula-editor/components/common/UILoadingSpinnerCentered';
import findObjectTypeIdsAssociatedTo from 'automation-ui-associations/utils/findObjectTypeIdsAssociatedTo';
import { useAssociationDefinitions } from 'automation-ui-associations/hooks/associationDefinitionsHooks';
import { WORKFLOWS_ALLOWED_CUSTOM_OBJECT_META_TYPE_IDS } from 'automation-ui-references/object-types/objectTypeUtils';
import { GET_ALL_OBJECT_TYPES } from 'automation-ui-references/object-type-properties/ObjectTypesQueries';
import { GET_ENROLLABLE_OBJECT_TYPES_FAS } from 'automation-ui-formula-editor/fetch-object-panel/queries/EnrollableWorkflowTypesQueries';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { PROPERTY_MATCHING_DATA_SOURCES } from 'automation-ui-formula-editor/constants/Gates';
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY_OPTIONS = [];
const AddDataSourceSelectTargetObject = ({
  flowObjectTypeId,
  onChange,
  objectTypeId,
  objectTypeFilter = () => true,
  readOnly
}) => {
  const isUngatedToPropertyMatching = useIsUngated(PROPERTY_MATCHING_DATA_SOURCES);
  const associationDefinitionsQuery = useAssociationDefinitions([flowObjectTypeId]);
  const {
    data: fasData,
    loading: fasLoading
  } = useQuery(GET_ENROLLABLE_OBJECT_TYPES_FAS, {
    skip: !isUngatedToPropertyMatching
  });
  const {
    data: allObjectTypesData,
    loading: allObjectTypesLoading
  } = useQuery(GET_ALL_OBJECT_TYPES);
  const isLoading = associationDefinitionsQuery.loading || allObjectTypesLoading || fasLoading;

  // Add in block list to pick off objects we don't want to use
  const blockListOfObjects = ['0-4'];
  const options = useMemo(() => {
    var _fasData$formulaEdito;
    if (isLoading || !allObjectTypesData) {
      return EMPTY_OPTIONS;
    }
    const enrollableObjectTypeIds = new Set(Object.keys((_fasData$formulaEdito = fasData === null || fasData === void 0 ? void 0 : fasData.formulaEditor_enrollableObjectTypesFAS) !== null && _fasData$formulaEdito !== void 0 ? _fasData$formulaEdito : {}));
    const associatedObjectTypeIds = new Set(findObjectTypeIdsAssociatedTo(flowObjectTypeId, associationDefinitionsQuery));
    return allObjectTypesData.automation_allObjectTypes.toArray().filter(objectType => {
      var _objectType$metaTypeI;
      const selectableObjectTypeId = objectType.getObjectTypeId();
      const isCustomObject = WORKFLOWS_ALLOWED_CUSTOM_OBJECT_META_TYPE_IDS.includes((_objectType$metaTypeI = objectType.metaTypeId) !== null && _objectType$metaTypeI !== void 0 ? _objectType$metaTypeI : NaN);
      const isAssociatedToFlowObjectType = associatedObjectTypeIds.has(selectableObjectTypeId);
      const isEnrollableObjectType = enrollableObjectTypeIds.has(selectableObjectTypeId);
      const isBlocklisted = blockListOfObjects.includes(selectableObjectTypeId) || !objectTypeFilter(selectableObjectTypeId);
      if (isUngatedToPropertyMatching) {
        return (isCustomObject || isAssociatedToFlowObjectType || isEnrollableObjectType) && !isBlocklisted;
      } else {
        return isAssociatedToFlowObjectType && !isBlocklisted;
      }
    }).map(objectType => {
      var _objectType$singularF;
      return {
        value: objectType.getObjectTypeId(),
        text: (_objectType$singularF = objectType.singularForm) !== null && _objectType$singularF !== void 0 ? _objectType$singularF : objectType.getObjectTypeId()
      };
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [allObjectTypesData, associationDefinitionsQuery.data, isLoading, flowObjectTypeId]);
  useEffect(() => {
    const [firstOption] = options;
    if (objectTypeId === null && options.length === 1) {
      onChange(firstOption.value);
    }
  }, [options, onChange, objectTypeId]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(UILoadingSpinnerCentered, {});
  }
  return /*#__PURE__*/_jsx(UIFormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.form-components.object-type-select.title"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(UISelect, {
      onChange: ({
        target: {
          value
        }
      }) => onChange(value),
      isLoading: isLoading,
      options: options,
      value: objectTypeId,
      "data-test-id": "fetch-object-object-select",
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.form-components.object-type-select.placeholder"
      }),
      readOnly: readOnly
    })
  });
};
export default AddDataSourceSelectTargetObject;